import "./module.css";

import { CloudUploadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useUploadEventGalllary } from "features/events/use-cases/upload-event-gallary";
import { useEffect, useRef, useState } from "react";
import { validateExactImageDimensions } from "utils/validations";

export const EventGallaryModal = ({ id, onClose, gallaryModal, refetch, activeTab }: { id: string; onClose: () => void; gallaryModal: boolean; refetch: () => void; activeTab: string }) => {
    const isEventDetails = activeTab === "2";
    const { uploadImage: uploadGallaryImage, loading } = useUploadEventGalllary();
    
    const [uploadFiles, setUploadFiles] = useState<{ file: File, dataURL: string }[]>([]);
    const [error, setError] = useState<string[]>([]);

    const handleClose = () => {
        uploadFiles.forEach(item => {
            if (item.file.type.startsWith('video/')) {
                URL.revokeObjectURL(item.dataURL);
            }
        });
        onClose();
        setUploadFiles([]);
        setError([]);
    };

    const handleSuccess = () => {
        handleClose();
        refetch();
    };

    const fileInputRef = useRef<any>();

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        const filesArray = Array.from(selectedFiles);
        // Reset file input value to allow selecting the same file again if needed
        e.target.value = '';

        const targetWidth = isEventDetails ? 1276 : 558;
        const targetHeight = isEventDetails ? 449 : 314;
        const contextName = isEventDetails ? "Event Detail" : "Event List";

        const processedFiles: { file: File; dataURL: string }[] = [];
        const errors: string[] = [];

        await Promise.all(
            filesArray.map(async (file) => {
                const isWebp = file.type === 'image/webp' || /\.webp$/i.test(file.name);
                if (isWebp) {
                    errors.push(
                        filesArray.length > 1
                            ? `${file.name}: WebP format is not supported. Please upload a JPEG or PNG image.`
                            : 'WebP format is not supported. Please upload a JPEG or PNG image.'
                    );
                } else if (file.type.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(file.name)) {
                    if (!isEventDetails) {
                        errors.push(
                            filesArray.length > 1
                                ? `${file.name}: Videos are not supported for Event List Images. Please upload a JPEG or PNG image.`
                                : 'Videos are not supported for Event List Images. Please upload a JPEG or PNG image.'
                        );
                    } else {
                        const videoPreviewUrl = URL.createObjectURL(file);
                        processedFiles.push({ file, dataURL: videoPreviewUrl });
                    }
                } else if (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || /\.(jpe?g|png)$/i.test(file.name)) {
                    const validation = await validateExactImageDimensions(
                        file,
                        targetWidth,
                        targetHeight,
                        contextName
                    );

                    if (!validation.isValid) {
                        const errorMsg = filesArray.length > 1
                            ? `${file.name}: ${validation.errorMessage}`
                            : validation.errorMessage || '';
                        errors.push(errorMsg);
                    } else {
                        const reader = new FileReader();
                        const dataURL = await new Promise<string>((resolve) => {
                            reader.onload = (ev) => resolve(ev?.target?.result as string);
                            reader.readAsDataURL(file);
                        });
                        processedFiles.push({ file, dataURL });
                    }
                } else {
                    errors.push(`${file.name}: Format not supported. Please upload a ${isEventDetails ? "JPEG, PNG, or MP4" : "JPEG or PNG"}.`);
                }
            })
        );

        setUploadFiles(processedFiles);
        setError(errors);
    };

    const handleUploadMedia = async () => {
        const typeId = activeTab === "1" ? "2" : activeTab === "2" ? "1" : "3";
        const formData = new FormData();
        
        uploadFiles.forEach((item) => {
            // Appends the actual File object (whether resized image or original video)
            formData.append(`files`, item.file, item.file.name);
        });

        uploadGallaryImage(id, typeId, formData, handleSuccess);
    };

    useEffect(() => {
        setUploadFiles([]);
        setError([]);
    }, [gallaryModal]);

    return (
        <>
            {gallaryModal ? (
                <div className='popup'>
                    <div className='modal_head'>
                        <h4>{activeTab === "2" ? "Upload Event Details Media" : activeTab === "3" ? "Upload Home Banner Image" : "Upload Event List Image"}</h4>
                        <button onClick={handleClose} className='close-btn'>&times;</button>
                    </div>
                    <div className='modal_body'>
                        {error.length > 0 && error.map((item, i) => (
                            <p key={i} style={{ color: "red", fontSize: '12px', margin: '4px 0' }}>{item}</p>
                        ))}

                        <div className="frmscrll">
                            <div className="imgup_wrap">
                                {uploadFiles.map((item, i) => (
                                    <div className="img_bx" key={i} style={{ overflow: 'hidden', borderRadius: '8px' }}>
                                        {item.file.type.startsWith('video/') ? (
                                            <video 
                                                src={item.dataURL} 
                                                autoPlay 
                                                muted 
                                                loop 
                                                playsInline
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                            />
                                        ) : (
                                            <img src={item.dataURL} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="upld_file_bx">
                                <CloudUploadOutlined />
                                <div className="upld_file">
                                    <label onClick={handleClick}>Choose Files </label>
                                    <span> {uploadFiles.length > 0 ? `${uploadFiles.length} files selected` : "No File Chosen"}</span>
                                    <input
                                        id="fileInput"
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        accept={isEventDetails ? "image/jpeg,image/png,video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png"}
                                        multiple
                                        onChange={handleFileChange}
                                    />
                                </div>
                                <p style={{ fontSize: '11px', color: '#666', marginTop: '6px', lineHeight: '16px' }}>
                                    Supported formats: {isEventDetails ? "JPEG, PNG, MP4." : "JPEG, PNG."}<br />
                                    {activeTab === "2"
                                        ? "Event Details Image: Exactly 1276 × 449 px"
                                        : "Event List Image: Exactly 558 × 314 px"}
                                </p>
                            </div>

                            <div className="upld_btn">
                                <Button 
                                    loading={loading} 
                                    className="save_btn" 
                                    disabled={uploadFiles.length < 1 || error.length > 0} 
                                    onClick={handleUploadMedia} 
                                    type="primary"
                                > 
                                    Save & Upload Media
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};
